import { ButtonPrimary } from 'components/Button'
import FilterRow from 'components/FilterRow'
import { DynamicSizedLoadingRow } from 'components/Loader/styled'
import Pagination, { SearchResultStats } from 'components/PaginationControls'
import { ResponsiveButtonPrimary } from 'components/PositionList/styled'
import { Spinner, SpinnerWithText } from 'components/Spinner'
import { MouseoverTooltip } from 'components/Tooltip'
import { useConnection } from 'hooks/useConnection'
import { useFilteredIslandsList } from 'hooks/useIslandsApi'
import useTheme from 'hooks/useTheme'
import { RefreshControl } from 'pages/Liquidity'
import { Header, NetworkIcon } from 'pages/Liquidity/Liquidity.styles'
import { OrderDirection } from 'queries/v3/generated'
import React, { memo, Suspense, useCallback, useEffect, useMemo } from 'react'
import { FaSort, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa'
import { useToggleWalletModal } from 'state/application/hooks'
import { IslandApiResponse } from 'state/data/slice'
import { BaseMemoryFilter } from 'state/filterTypes'
import { useAppDispatch } from 'state/hooks'
import { useAppSelector } from 'state/hooks'
import { useSearchTerm } from 'state/kodiakIslands/hooks'
import { setSortBy, toggleFilter } from 'state/kodiakIslands/reducer'
import { updateQueryParams } from 'state/kodiakIslands/reducer'
import { IslandOrderBy } from 'state/kodiakIslands/types'
import styled from 'styled-components'
import { ThemedText } from 'theme'

import IslandListItem from './IslandListItem'
import SearchBanner from './SearchBanner'
import * as S from './styled'
import { ErrorContainer, SortIconWrapper } from './styled'

const DataTransitionOverlay = styled(S.SortingOverlay)`
  background-color: ${({ theme }) => theme.cardPrimary};
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 0.9;
    }
  }
`

const SortBothIcon = ({ active }: { active?: boolean }) => (
  <SortIconWrapper active={active}>
    <FaSort />
  </SortIconWrapper>
)

const SortAscIcon = () => (
  <SortIconWrapper active={true}>
    <FaSortAmountUp />
  </SortIconWrapper>
)

const SortDescIcon = () => (
  <SortIconWrapper active={true}>
    <FaSortAmountDown />
  </SortIconWrapper>
)

const ITEMS_PER_PAGE = 20

const islandsLoader = () => (
  <>
    <DynamicSizedLoadingRow
      height="30em"
      style={{ width: '100%' }}
      borderRadius="16px"
      borderTopLeftRadius="0px"
      borderTopRightRadius="0px"
      borderBottomLeftRadius="16px"
      borderBottomRightRadius="16px"
    >
      <div />
    </DynamicSizedLoadingRow>
  </>
)

const getSortIcon = (columnOrderBy: IslandOrderBy, currentOrderBy: IslandOrderBy, orderDirection: OrderDirection) => {
  if (columnOrderBy !== currentOrderBy) {
    return <SortBothIcon active={false} />
  }

  return orderDirection === OrderDirection.Desc ? <SortDescIcon /> : <SortAscIcon />
}

const IslandContent = memo(
  ({
    islandsToRender,
    isInTransition,
  }: {
    islandsToRender: IslandApiResponse[]
    isInTransition?: boolean
    isPaging?: boolean
  }) => {
    const visibleIslands = useMemo(() => {
      if (!islandsToRender || !Array.isArray(islandsToRender)) {
        return []
      }
      return islandsToRender.filter(Boolean)
    }, [islandsToRender])

    if (isInTransition) {
      return (
        <tbody>
          <tr>
            <td colSpan={7} style={{ width: '100%' }}>
              {islandsLoader()}
            </td>
          </tr>
        </tbody>
      )
    }

    return (
      <tbody>
        {visibleIslands.map((island: IslandApiResponse, index: number) => (
          <IslandListItem island={island} row={index} key={island.id} />
        ))}
      </tbody>
    )
  }
)
IslandContent.displayName = 'IslandContent'

const ListHeader = memo(
  ({
    theme,
    orderBy,
    orderDirection,
    handleSort,
    isInTransition,
    rewardVaultRewardTokenSymbol,
  }: {
    theme: any
    orderBy: IslandOrderBy
    orderDirection: OrderDirection
    handleSort: (newOrderBy: IslandOrderBy) => void
    isInTransition?: boolean
    rewardVaultRewardTokenSymbol?: string
  }) => {
    const rewardVaultLabel = rewardVaultRewardTokenSymbol
      ? `${rewardVaultRewardTokenSymbol} Reward Vault`
      : 'Reward Vault'

    const onSortClick = useCallback(
      (newOrderBy: IslandOrderBy) => {
        if (!isInTransition) {
          handleSort(newOrderBy)
        }
      },
      [handleSort, isInTransition]
    )

    return (
      <thead>
        <tr>
          <S.Th width="25%">
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  Automated Liquidity Management Vault
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                Pool
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>

          <S.Th width="20%">
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  Range in which liquidity is deployed
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                Range
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>

          <S.Th width="15%">
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  Current pool price
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                Price
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>

          <S.Th
            width="15%"
            onClick={() => onSortClick(IslandOrderBy.poolTVL)}
            sortable
            active={orderBy === IslandOrderBy.poolTVL}
            role="columnheader"
            aria-sort={
              orderBy === IslandOrderBy.poolTVL
                ? orderDirection === OrderDirection.Desc
                  ? 'descending'
                  : 'ascending'
                : undefined
            }
          >
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  Total dollar TVL in the island
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                Pool TVL {getSortIcon(IslandOrderBy.poolTVL, orderBy, orderDirection)}
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>

          <S.Th
            width="15%"
            onClick={() => onSortClick(IslandOrderBy.FarmTVL)}
            sortable
            active={orderBy === IslandOrderBy.FarmTVL}
            role="columnheader"
            aria-sort={
              orderBy === IslandOrderBy.FarmTVL
                ? orderDirection === OrderDirection.Desc
                  ? 'descending'
                  : 'ascending'
                : undefined
            }
          >
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  Total dollar TVL in the Kodiak Farm or {rewardVaultLabel}
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                Farm TVL {getSortIcon(IslandOrderBy.FarmTVL, orderBy, orderDirection)}
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>

          <S.Th
            width="20%"
            onClick={() => onSortClick(IslandOrderBy.AprAverageApr)}
            sortable
            active={orderBy === IslandOrderBy.AprAverageApr}
            role="columnheader"
            aria-sort={
              orderBy === IslandOrderBy.AprAverageApr
                ? orderDirection === OrderDirection.Desc
                  ? 'descending'
                  : 'ascending'
                : undefined
            }
          >
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  Average APR based on fees generated by the pool and/or {rewardVaultRewardTokenSymbol || 'reward'} /
                  farm emissions
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                APR {getSortIcon(IslandOrderBy.AprAverageApr, orderBy, orderDirection)}
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>

          <S.Th
            width="20%"
            onClick={() => onSortClick(IslandOrderBy.Balance)}
            sortable
            active={orderBy === IslandOrderBy.Balance}
            role="columnheader"
            aria-sort={
              orderBy === IslandOrderBy.Balance
                ? orderDirection === OrderDirection.Desc
                  ? 'descending'
                  : 'ascending'
                : undefined
            }
          >
            <MouseoverTooltip
              text={
                <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                  User deposits in the island
                </ThemedText.DeprecatedBody>
              }
            >
              <ThemedText.DeprecatedBody
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                color={theme.textSecondary}
                className="title"
              >
                Holdings {getSortIcon(IslandOrderBy.Balance, orderBy, orderDirection)}
              </ThemedText.DeprecatedBody>
            </MouseoverTooltip>
          </S.Th>
        </tr>
      </thead>
    )
  }
)
ListHeader.displayName = 'ListHeader'

export default function IslandList({ refreshControl }: { refreshControl?: RefreshControl }) {
  const theme = useTheme()
  const { account } = useConnection()
  const toggleWalletModal = useToggleWalletModal()
  const searchTerm = useSearchTerm()
  const showConnectAWallet = Boolean(!account)
  const dispatch = useAppDispatch()

  const { orderBy, orderDirection, filters } = useAppSelector((state) => state.kodiakIslands)

  useEffect(() => {
    if (!orderBy) {
      dispatch(
        setSortBy({
          orderBy: IslandOrderBy.AprAverageApr,
          orderDirection: OrderDirection.Desc,
        })
      )
    }
  }, [dispatch, orderBy])

  const {
    filteredIslands,
    isLoading,
    smartFiltersUsed,
    isDataTransition,
    transitionType,
    isError,
    error,
    refetch,
    pagination: { currentPage, goToPage, totalPages, limit },
  } = useFilteredIslandsList({
    orderBy,
    orderDirection,
  })

  useEffect(() => {
    if (refreshControl?.onRegister && refetch) {
      const asyncRefetch = async () => {
        refetch()
      }
      refreshControl.onRegister(asyncRefetch)
    }

    return () => {
      if (refreshControl?.onRegister) {
        refreshControl.onRegister(null)
      }
    }
  }, [refreshControl?.onRegister, refetch])

  const handlePageChange = useCallback(
    (page: number) => {
      if (isDataTransition) return
      goToPage(page - 1)
      window.scrollTo(0, 0)
    },
    [goToPage, isDataTransition]
  )

  const handleSort = useCallback(
    (newOrderBy: IslandOrderBy) => {
      if (isDataTransition) return

      const newDirection =
        orderBy === newOrderBy && orderDirection === OrderDirection.Desc ? OrderDirection.Asc : OrderDirection.Desc

      dispatch(
        setSortBy({
          orderBy: newOrderBy,
          orderDirection: newDirection,
        })
      )

      dispatch(
        updateQueryParams({
          orderBy: newOrderBy,
          orderDirection: newDirection,
          offset: 0,
          limit,
        })
      )
    },
    [isDataTransition, dispatch, orderBy, orderDirection, limit]
  )

  const handleFilterChange = useCallback(
    (filter: BaseMemoryFilter) => {
      if (!isDataTransition) {
        dispatch(toggleFilter(filter.displayName))
      }
    },
    [dispatch, isDataTransition]
  )

  const getTransitionLabel = () => {
    if (refreshControl?.isRefreshing) {
      return 'Refreshing pools...'
    }
    if (transitionType === 'sort') {
      if (orderBy === IslandOrderBy.AprAverageApr) return 'Sorting by APR...'
      if (orderBy === IslandOrderBy.poolTVL) return 'Sorting by Pool TVL...'
      if (orderBy === IslandOrderBy.FarmTVL) return 'Sorting by Farm TVL...'
      if (orderBy === IslandOrderBy.Balance) return 'Sorting by Holdings...'
      return 'Sorting...'
    } else if (transitionType === 'filter') {
      return 'Applying filters...'
    } else if (transitionType === 'search') {
      return 'Searching pools...'
    }
    return 'Loading pools...'
  }

  const showTransitionOverlay = isDataTransition || refreshControl?.isRefreshing || (isLoading && !isError)
  const showError = isError && !showTransitionOverlay
  const showContent = !showTransitionOverlay && !showError
  const rewardVaultRewardTokenSymbol = useMemo(() => {
    return filteredIslands.find((island) => island?.isRewardVault && island.farm?.rewardTokens?.[0]?.symbol)?.farm
      ?.rewardTokens?.[0]?.symbol
  }, [filteredIslands])

  return (
    <S.PageWrapper>
      <FilterRow filters={filters} onFilterChange={handleFilterChange} />

      {smartFiltersUsed && <SearchBanner hasResults={!!filteredIslands.length} />}

      <S.ListContentWrapper>
        <S.TableWrapper>
          {showTransitionOverlay && (
            <DataTransitionOverlay>
              <SpinnerWithText>
                <Spinner />
                <ThemedText.DeprecatedBody color="white">{getTransitionLabel()}</ThemedText.DeprecatedBody>
              </SpinnerWithText>
            </DataTransitionOverlay>
          )}

          <S.Table>
            {!showError ? (
              <ListHeader
                theme={theme}
                orderBy={orderBy}
                orderDirection={orderDirection}
                handleSort={handleSort}
                isInTransition={showTransitionOverlay}
                rewardVaultRewardTokenSymbol={rewardVaultRewardTokenSymbol}
              />
            ) : (
              ''
            )}

            {showError ? (
              <tbody>
                <tr>
                  <td colSpan={7}>
                    <Header style={{ border: 'none' }}>
                      <ErrorContainer>
                        <ThemedText.DeprecatedBody color={theme.deprecated_text3} textAlign="center">
                          <NetworkIcon />
                          <div style={{ marginTop: 8 }}>
                            {error?.message || 'Something went wrong while loading top pools.'}
                          </div>
                        </ThemedText.DeprecatedBody>

                        <ResponsiveButtonPrimary style={{ marginTop: '1rem' }} onClick={() => refetch()}>
                          Try again
                        </ResponsiveButtonPrimary>
                      </ErrorContainer>
                    </Header>
                  </td>
                </tr>
              </tbody>
            ) : showTransitionOverlay ? (
              <tbody>
                <tr>
                  <td colSpan={7} style={{ height: '400px', width: '100%' }}></td>
                </tr>
              </tbody>
            ) : (
              <>
                {filteredIslands && filteredIslands.length > 0 ? (
                  <Suspense
                    fallback={
                      <tbody>
                        <tr>
                          <td colSpan={7} style={{ height: '400px', width: '100%' }}></td>
                        </tr>
                      </tbody>
                    }
                  >
                    <IslandContent islandsToRender={filteredIslands} isInTransition={false} />
                  </Suspense>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan={7}>
                        <S.ErrorContainer>
                          <ThemedText.DeprecatedBody color={theme.deprecated_text3} textAlign="center">
                            <S.InboxIcon strokeWidth={1} />
                            <div>No islands found.</div>
                          </ThemedText.DeprecatedBody>
                          {showConnectAWallet && (
                            <ButtonPrimary
                              style={{ marginTop: '2em', padding: '8px 16px', width: '140px' }}
                              onClick={toggleWalletModal}
                            >
                              Connect wallet
                            </ButtonPrimary>
                          )}
                        </S.ErrorContainer>
                      </td>
                    </tr>
                  </tbody>
                )}
              </>
            )}
          </S.Table>

          {showContent && filteredIslands.length > 0 && (
            <Pagination currentPage={currentPage + 1} totalPages={totalPages} onPageChange={handlePageChange} />
          )}

          {searchTerm && showContent && filteredIslands.length > 0 && (
            <SearchResultStats>
              Showing {filteredIslands.length > 0 ? currentPage * ITEMS_PER_PAGE + 1 : 0}-
              {Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalPages)} of {totalPages} results
            </SearchResultStats>
          )}
        </S.TableWrapper>
      </S.ListContentWrapper>
    </S.PageWrapper>
  )
}
